import { EOB_SPECIAL_CASES, PROVIDER_PROFESSION_OPTIONS, statusMapping, truRevSubStatusOptions } from "Common/Constants";
import moment from "moment-mini";
import MaskPhoneNumber from "./MaskPhoneNumber";
import { LATE_CANCEL_WINDOW_MS } from "./IntakeAdmin/appointmentStatusDefinitions";

export const GetFirstInitials = (name) => {
  return name.split(" ").reduce((a, b) => (a += b[0]), "");
};

export const Base64ToBlob = (base64, type = "application/octet-stream") => {
  const binStr = atob(base64);
  const len = binStr.length;
  const arr = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    arr[i] = binStr.charCodeAt(i);
  }
  return new Blob([arr], { type: type });
};
export function toOrdinal(num) {
  const lastDigit = num % 10;
  const lastTwoDigits = num % 100;

  if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
    return num + "th";
  }

  switch (lastDigit) {
    case 0:
      return num + "";
    case 1:
      return num + "st";
    case 2:
      return num + "nd";
    case 3:
      return num + "rd";
    default:
      return num + "th";
  }
}
export const maskEmail = (email) => {
  const [localPart, domain] = email.split("@");
  if (!localPart || !domain) return email; // invalid email, return as is

  const firstChar = localPart[0];
  const masked = firstChar + "*".repeat(localPart.length - 1);
  return `${masked}@${domain}`;
};
export const lowercaseKeys = (obj) => {
  const newObj = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      newObj[key.toLowerCase()] = obj[key];
    }
  }
  return newObj;
};

export const ResizeImage = (originalHeight, originalWidth, newHeight) => {
  const newWidth = (newHeight / originalHeight) * originalWidth;
  return newWidth;
};

export const GetInsurancePlan = (array) => {
  const priorityOrder = ["P", "S", "T"];
  for (const priority of priorityOrder) {
    const foundItem = array.find((item) => item.coverage === priority);
    if (foundItem) {
      return foundItem;
    }
  }
  return [];
};

export const get2FAMethodAndPath = (res) => {
  const methods = {
    authenticator: res.twoFactorViaAuthenticator,
    email: res.twoFactorViaEmail,
    text: res.twoFactorViaText,
  };

  let path = "/two-factor-auth";
  let methodName;

  const trueCount = Object.values(methods).filter(Boolean).length;

  if (trueCount === 1) {
    const [key] = Object.entries(methods).find(([_, value]) => value);
    methodName = key;
    path = "/verify-otp";
  }

  return { path, methodName };
};

/**
 * Triggers a download in the browser for the given file URL.
 * @param {string} url - The URL of the file to download.
 * @param {string} [filename] - Optional filename for the downloaded file.
 */
export const downloadFileFromUrl = (url, filename = "") => {
  if (!url) return;
  const link = document.createElement("a");
  link.href = url;
  if (filename) {
    link.setAttribute("download", filename);
  } else {
    link.setAttribute("download", "");
  }
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
export const parseAmount = (amount) =>
  parseFloat(String(amount).replace(/,/g, "")) || 0;

export const formatFieldName = (field) => {
  if (EOB_SPECIAL_CASES[field]) {
    return EOB_SPECIAL_CASES[field];
  }

  // For other fields, add spaces between words and capitalize each word
  return field
    .replace(/_/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};
export const disallowedStatusCodes = ["N", "D", "RS"];
export const shouldEnableClaim = (claimModel) => {
  const codes = ["N", "RS", "D", "ACK_277_D", "SP"];
  if (claimModel.forceUnlock) return false;
  if (!claimModel.id) return false;

  const { claimStatusCode, primaryStatus } = claimModel;


  const isDisallowed =
    codes.includes(claimStatusCode) ||
    disallowedStatusCodes.includes(primaryStatus) ||
    (claimStatusCode === "ACK_277_D" && primaryStatus === "B");

  return !isDisallowed;
};

export const formatDateTime = (dateString) => {
  return dateString ? moment(dateString).format("MM/DD/YYYY hh:mm A") : "N/A";
};

export const formatDateTimeWithMonthName = (dateString) => {
  return dateString
    ? moment(dateString).format("MMM DD, YYYY, hh:mm A")
    : "N/A";
};

export const extractYMD = (dateStr) => {
  if (dateStr) {
    const [year, month, day] = dateStr.split("-");
    return {
      year,
      month: month?.padStart(2, "0"),
      day: day?.padStart(2, "0"),
    };
  } else {
    return {
      year: "",
      month: "",
      day: "",
    };
  }
};

export const strengthLabel = (strength) => {
  switch (strength) {
    case 0:
      return "";
    case 1:
      return "Weak";
    case 2:
      return "Medium";
    case 3:
      return "Good";
    case 4:
      return "Strong";
    default:
      return "";
  }
};

export const strengthColor = (strength) => {
  switch (strength) {
    case 1:
      return "#ef4444"; // red
    case 2:
      return "#eab308"; // yellow
    case 3:
      return "#3b82f6"; // blue
    case 4:
      return "#22c55e"; // green
    default:
      return "#e5e7eb"; // gray
  }
};
export const encodeQueryParam = (value) =>
  encodeURIComponent(value).replace(/\+/g, "%2B");
export const strengthTextColor = (strength) => {
  switch (strength) {
    case 1:
      return "text-red-500";
    case 2:
      return "text-yellow-500";
    case 3:
      return "text-blue-500";
    case 4:
      return "text-green-500";
    default:
      return "text-gray-400";
  }
};

export const calculateColumnTotals = (charges) => {
  return charges.reduce(
    (totals, charge) => {
      totals.writeOffAmount += Number(charge.writeOffAmount) || 0;
      totals.otherPR += Number(charge.otherPR) || 0;
      totals.coInsuranceAmount += Number(charge.coInsuranceAmount) || 0;
      totals.copayAmount += Number(charge.copayAmount) || 0;
      totals.deductibleAmount += Number(charge.deductableAmount) || 0; // Watch for typo!
      totals.paidAmount += Number(charge.paidAmount) || 0;
      totals.allowedAmount += Number(charge.allowedAmount) || 0;
      totals.billedAmount += Number(charge.billedAmount) || 0;
      totals.actualBilledAmount += Number(charge.actualBilledAmount) || 0;
      return totals;
    },
    {
      writeOffAmount: 0,
      otherPR: 0,
      coInsuranceAmount: 0,
      copayAmount: 0,
      deductibleAmount: 0,
      paidAmount: 0,
      allowedAmount: 0,
      billedAmount: 0,
      actualBilledAmount: 0,
    }
  );
};

export const calculateTotalBalance = (chargeRows, calculateBalanceAmount) => {
  const balances = [];
  for (let index = 0; index < chargeRows.length; index++) {
    const bal = calculateBalanceAmount(chargeRows[index]);
    balances.push(bal);
  }
  return balances.reduce((sum, bal) => sum + Number(bal || 0), 0);
};
export const performChargeMatching = (eraLineItems, systemCharges) => {
  const newMatchedCharges = eraLineItems.map((eraLine) => ({
    eraLineItem: {
      ...eraLine,
      units: eraLine.units === 0 ? 1 : eraLine.units,
    },
    systemCharge: null,
    matchStatus: "unmatched",
  }));

  const availableSystemCharges = [...systemCharges]; // Create a mutable copy

  newMatchedCharges.forEach((matchedEra) => {
    const era = matchedEra.eraLineItem;
    const foundIndex = availableSystemCharges.findIndex(
      (sysCharge) =>
        sysCharge.procCode === era.procCode &&
        sysCharge.units == era.units &&
        sysCharge.billedAmount === era.billedAmount
      // !sysCharge.isMatched
    );

    if (foundIndex !== -1) {
      const matchedSysCharge = availableSystemCharges[foundIndex];
      matchedEra.systemCharge = matchedSysCharge;
      matchedEra.matchStatus = "matched";
      matchedSysCharge.isMatched = true; // Mark as matched
      availableSystemCharges.splice(foundIndex, 1); // Remove from available
    }
  });

  return newMatchedCharges;
};
export const getFilteredIns = (visitIns, patientIns) => {
  // insurance priority order
  const priority = ["p", "s", "t"];

  // collect visit coverage types
  const visitCoverage = new Set(visitIns.map((v) => v.coverage.toLowerCase()));

  // iterate patientIns in priority order
  for (let cov of priority) {
    // if this coverage is not already used in visitIns
    if (!visitCoverage.has(cov)) {
      // find patient insurance with that coverage
      const found = patientIns.find((p) => p.coverage.toLowerCase() === cov);
      if (found) return found;
    }
  }

  // nothing found
  return null;
};

// Sent Via utility functions
export const getSentViaIcon = (method) => {
  const methodLower = method?.trim().toLowerCase();
  if (methodLower === "email") {
    return "mail"; // Return string identifier for icon component
  } else if (methodLower === "text" || methodLower === "sms") {
    return "chat";
  } else if (methodLower === "mail") {
    return "mailDelivery";
  }
  return null;
};

export const getSentViaPillStyle = (method) => {
  const methodLower = method?.trim().toLowerCase();
  if (methodLower === "email") {
    return "bg-green-50 text-green-700 border-green-200 hover:bg-green-100";
  } else if (methodLower === "text" || methodLower === "sms") {
    return "bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100";
  } else if (methodLower === "mail") {
    return "bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100";
  }
  return "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100";
};

export const maskPhoneInText = (text) => {
  if (!text || typeof text !== "string") return text;
  const phoneRegex = /\b(\d{10})\b/g;
  return text.replace(phoneRegex, (match) => {
    return MaskPhoneNumber(match);
  });
};

export const formatSentViaData = (sentViaData) => {
  if (!sentViaData || sentViaData.trim() === "" || !sentViaData.includes(",")) {
    return null;
  }

  const [method, contact] = sentViaData.split(",", 2);
  const maskedContact = maskPhoneInText(contact?.trim());

  return {
    method: method?.trim(),
    contact: maskedContact,
    iconType: getSentViaIcon(method?.trim()),
    pillStyle: getSentViaPillStyle(method?.trim()),
  };
};

export const renderSentViaIcon = (iconType, className = "w-4 h-4") => {
  const mailIcon = require("../SVGS/MailIcon").default;
  const chatIcon = require("../SVGS/ChatIcon").default;
  const mailDeliveryIcon = require("../SVGS/MailDeliveryIcon").default;

  switch (iconType) {
    case "mail":
      return mailIcon({ className });
    case "chat":
      return chatIcon({ className });
    case "mailDelivery":
      return mailDeliveryIcon({ className });
    default:
      return null;
  }
};

// Add New Patient Payment Popup Utils ..... AddNewPatientPaymentPopup.jsx
export const isDiscountExceedingPayment = (paymentModel, lineItems) => {
  if (!paymentModel.isDiscount) return false;
  const subtotal = calculateSubtotal(lineItems);
  const discount = calculateDiscount(paymentModel, lineItems);
  return discount > subtotal;
};
export const calculateSubtotal = (lineItems) => {
  return lineItems.reduce(
    (sum, item) => parseFloat(sum) + parseFloat(item.amount || 0),
    0
  );
};

export const findModelDifferences = (initial, updated, path = "") => {
  const differences = [];

  if (!initial && !updated) return differences;
  if (!initial) {
    differences.push({
      path: path || "root",
      type: "added",
      oldValue: undefined,
      newValue: updated,
    });
    return differences;
  }
  if (!updated) {
    differences.push({
      path: path || "root",
      type: "removed",
      oldValue: initial,
      newValue: undefined,
    });
    return differences;
  }

  // Handle arrays
  if (Array.isArray(initial) || Array.isArray(updated)) {
    const initialArr = Array.isArray(initial) ? initial : [];
    const updatedArr = Array.isArray(updated) ? updated : [];

    if (initialArr.length !== updatedArr.length) {
      differences.push({
        path: path || "root",
        type: "arrayLengthChanged",
        oldValue: initialArr.length,
        newValue: updatedArr.length,
      });
    }

    // Compare array items
    const maxLength = Math.max(initialArr.length, updatedArr.length);
    for (let i = 0; i < maxLength; i++) {
      const ITEM_PATH = `${path}[${i}]`;
      if (i >= initialArr.length) {
        differences.push({
          path: ITEM_PATH,
          type: "added",
          oldValue: undefined,
          newValue: updatedArr[i],
        });
      } else if (i >= updatedArr.length) {
        differences.push({
          path: ITEM_PATH,
          type: "removed",
          oldValue: initialArr[i],
          newValue: undefined,
        });
      } else {
        differences.push(
          ...findModelDifferences(initialArr[i], updatedArr[i], ITEM_PATH)
        );
      }
    }
    return differences;
  }

  // Handle objects
  if (
    typeof initial === "object" &&
    typeof updated === "object" &&
    initial !== null &&
    updated !== null
  ) {
    const allKeys = new Set([
      ...Object.keys(initial),
      ...Object.keys(updated),
    ]);

    allKeys.forEach((key) => {
      const newPath = path ? `${path}.${key}` : key;

      if (!(key in initial)) {
        differences.push({
          path: newPath,
          type: "added",
          oldValue: undefined,
          newValue: updated[key],
        });
      } else if (!(key in updated)) {
        differences.push({
          path: newPath,
          type: "removed",
          oldValue: initial[key],
          newValue: undefined,
        });
      } else {
        const initialVal = initial[key];
        const updatedVal = updated[key];

        if (
          typeof initialVal === "object" &&
          typeof updatedVal === "object" &&
          initialVal !== null &&
          updatedVal !== null
        ) {
          differences.push(
            ...findModelDifferences(initialVal, updatedVal, newPath)
          );
        } else if (initialVal !== updatedVal) {
          differences.push({
            path: newPath,
            type: "changed",
            oldValue: initialVal,
            newValue: updatedVal,
          });
        }
      }
    });

    return differences;
  }

  // Handle primitive values
  if (initial !== updated) {
    differences.push({
      path: path || "root",
      type: "changed",
      oldValue: initial,
      newValue: updated,
    });
  }

  return differences;
};
export const calculateDiscount = (paymentModel, lineItems) => {
  if (!paymentModel.isDiscount) return 0;
  const subtotal = calculateSubtotal(lineItems);
  if (paymentModel.discountType === "PER") {
    return (subtotal * paymentModel.discountValue) / 100;
  }
  return paymentModel.discountValue;
};
export const calculateCreditCardFee = (
  paymentModel,
  lineItems,
  ccFeePercentage
) => {
  if (
    paymentModel.isCCFees &&
    (paymentModel.patientPaymentType === "CC_PMT" ||
      paymentModel.patientPaymentType === "CC_RDR" ||
      paymentModel.patientPaymentType === "PAY_REQ")
  ) {
    const subtotal = calculateSubtotal(lineItems);
    return (subtotal * ccFeePercentage) / 100;
  }
  return 0;
};

export const calculateTotalDue = (paymentModel, lineItems) => {
  const subtotal = calculateSubtotal(lineItems);
  const discount = calculateDiscount(paymentModel, lineItems);
  const fee = calculateCreditCardFee(paymentModel, lineItems, 2.7);
  return subtotal - discount + fee;
};
export const calculatePatientResp = (claim) => {
  // PT RESP = COPAY + DEDUCTIBLE + COINSURANCE
  const totalCopay =
    claim.SERVICES?.reduce((sum, service) => {
      return sum + (parseFloat(service.COPAYAMOUNT) || 0);
    }, 0) || 0;

  const totalDeductible =
    claim.SERVICES?.reduce((sum, service) => {
      return sum + (parseFloat(service.DEDUCTIBLEAMOUNT) || 0);
    }, 0) || 0;

  const totalCoinsurance =
    claim.SERVICES?.reduce((sum, service) => {
      return sum + (parseFloat(service.COINSURANCEAMOUNT) || 0);
    }, 0) || 0;

  const patientResp = totalCopay + totalDeductible + totalCoinsurance;
  return patientResp.toFixed(2);
};

// Helper function to calculate total paid amount from all claims
export const calculateTotalPaidAmount = (claims) => {
  let totalPaid = 0;
  claims.forEach((claim) => {
    const services = claim.SERVICES || [];
    services.forEach((service) => {
      totalPaid += parseAmount(service.PROVIDERPAID);
    });
  });
  return totalPaid;
};

/**
 * Checks if a value is empty, null, undefined, or zero
 * @param {*} value - The value to check
 * @returns {boolean} - Returns true if value is empty/null/undefined/zero, false otherwise
 */
export const isEmptyOrZero = (value) => {
  // Check for null, undefined, or empty string
  if (value === null || value === undefined || value === "") {
    return true;
  }
  // Check for whitespace-only strings
  if (typeof value === "string" && value.trim() === "") {
    return true;
  }
  // Check if value parses to 0 or is NaN
  const parsed = parseFloat(value);
  return isNaN(parsed) || parsed === 0;
};

export const getCardBrandStyles = (brand) => {
  if (!brand) return "bg-neutral-700";
  switch (brand.toLowerCase()) {
    case "visa":
      return "bg-blue-700";
    case "mastercard":
      return "bg-red-600";
    case "amex":
    case "american express":
      return "bg-blue-500";
    case "discover":
      return "bg-orange-600";
    default:
      return "bg-neutral-700";
  }
};


export const getStatusOptions = (StatusCode) => {
  const status = StatusCode;
  // Only use flat() for "ALL" case and remove duplicates
  if (!status || status === "ALL" || status === "") {
    const allOptions = Object.values(truRevSubStatusOptions).flat();
    // Remove duplicates based on value property
    const uniqueOptions = allOptions.filter(
      (option, index, self) =>
        index === self.findIndex((o) => o.value === option.value)
    );
    return uniqueOptions;
  }
  // Conditional mapping based on status
  const optionKey = statusMapping[status];
  if (optionKey && truRevSubStatusOptions[optionKey]) {
    return truRevSubStatusOptions[optionKey];
  }

  // Default: return empty array if no mapping found
  return [];
};

export const getShortSummary = (hours) => {
  const openDays = hours.filter(h => !h.isClosed);
  const closedDays = hours.filter(h => h.isClosed);

  if (openDays.length === 0) return 'Closed';
  if (openDays.length === 7) return '7 days/week';

  // Check if Saturday (6) and Sunday (0) are closed and all weekdays (1-5) are open
  const closedDayNumbers = closedDays.map(h => h.dayOfWeek);
  const openDayNumbers = openDays.map(h => h.dayOfWeek);
  const isSaturdayClosed = closedDayNumbers.includes(6);
  const isSundayClosed = closedDayNumbers.includes(0);
  const allWeekdaysOpen = [1, 2, 3, 4, 5].every(day => openDayNumbers.includes(day));

  if (openDays.length === 5 && isSaturdayClosed && isSundayClosed && allWeekdaysOpen) {
    return 'Mon-Fri';
  }

  return `${openDays.length} days/week`;

};
export const containsSimulatedKeyword = (str) => {
  if (!str || typeof str !== 'string') return false;
  return str.toLowerCase().includes('simulated');
};


export const getProfessionLabel = (professionValue) => {
  if (!professionValue) return '';

  for (const group of PROVIDER_PROFESSION_OPTIONS) {
    const found = group.options.find(
      (option) => option.value === professionValue
    );

    if (found) {
      return found.label;
    }
  }

  return '';
};
export const getAppointmentStartMs = (encounter) => {
  const d = encounter?.appointment?.appointment_date;
  const t = encounter?.appointment?.appointment_time || '00:00';
  const m = moment(`${d} ${t}`, 'YYYY-MM-DD HH:mm', true);
  return m.isValid() ? m.valueOf() : Date.now();
}

export const resolveTargetStatus = (encounter, requestedStatus) => {
  if (requestedStatus !== 'cancelled') {
      return { status: requestedStatus };
  }
  const startMs = getAppointmentStartMs(encounter);
  const now = Date.now();
  if (Math.abs(now - startMs) <= LATE_CANCEL_WINDOW_MS) {
      return { status: 'late_cancelled' };
  }
  return { status: 'cancelled' };
};

/** Comma-separated responsible party string → trimmed values (chargemaster multi-select storage). */
export const parseResponsiblePartyValues = (responsibleParty) =>
  String(responsibleParty ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

/** True when "Self Pay" is selected (matches CHARGEMASTER_RESPONSIBLE_PARTY_OPTIONS value). */
export const isSelfPayInResponsibleParty = (responsibleParty) =>
  parseResponsiblePartyValues(responsibleParty).includes('Self Pay');

export const formatDateForInput = (value) => {
  if (!value) return '';
  if (typeof value === 'string') {
      return value.includes('T') ? value.split('T')[0] : value;
  }
  try {
      return new Date(value).toISOString().split('T')[0];
  } catch (error) {
      return '';
  }
};

/** Coerce API numeric values for controlled inputs (CustomInput uses `value || ""`, so 0 must be "0"). */
export const toInputString = (value) =>
  value === null || value === undefined || value === '' ? '' : String(value);

/** API returns an array; first row is usually "Please Select" → map to "All" with null value. */
export const mapDropdownOptions = (options) => {
  if (!Array.isArray(options)) return [];
  return options.map((item, index) => {
      const isPlaceholder =
          index === 0 &&
          (item.label === 'Please Select' || item.value === '' || item.value == null);
      if (isPlaceholder) {
          return { id: 'all', value: null, label: 'All' };
      }
      return item;
  });
};

